import React, { useState, useEffect, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import { EditOutlined, VisibilityOutlined, DeleteOutlined, Add } from "@mui/icons-material"
import { Button, Chip, IconButton, Typography, Box, CircularProgress, Tooltip } from "@mui/material"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import SensorDetailsModal from "../../components/SensorDetailsModal"
import { hasPermission, PERMISSIONS } from "../../utils/permissions"
import { useAuth } from "../../context/AuthContext"

const Sensors = () => {
  const navigate = useNavigate()
  const { user } = useAuth();  // Get the whole user object
  const userPermissions = user?.permissions;  // Safely access permissions
  const [sensors, setSensors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(5)
  const [totalItems, setTotalItems] = useState(0)
  const [sensorTypes, setSensorTypes] = useState({})
  const [selectedSensor, setSelectedSensor] = useState(null)
  const [selectedSensorType, setSelectedSensorType] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sensorTypesAvailable, setSensorTypesAvailable] = useState(false)

  const fetchSensors = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(API_ENDPOINTS.SENSOR.GET_ALL, {
        params: { page: page + 1, limit: pageSize },
      })
      setSensors(response.data.sensors)
      setTotalItems(response.data.totalItems)
      setLoading(false)
    } catch (err) {
      console.error("Error fetching sensors:", err)
      setError("Failed to fetch sensors. Please try again.")
      setLoading(false)
    }
  }, [page, pageSize])

  useEffect(() => {
    fetchSensors()
  }, [fetchSensors])

  useEffect(() => {
    const fetchSensorTypes = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ALL)
        const sensorTypesMap = {}
        response.data.sensorTypes.forEach((type) => {
          sensorTypesMap[type.id] = type.name
        })
        setSensorTypes(sensorTypesMap)
        setSensorTypesAvailable(response.data.sensorTypes.length > 0)
      } catch (err) {
        console.error("Error fetching sensor types:", err)
        setError("Failed to fetch sensor types. Please try again.")
        setSensorTypesAvailable(false)
      }
    }

    fetchSensorTypes()
  }, [])

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this sensor?")) {
      try {
        await api.delete(API_ENDPOINTS.SENSOR.DELETE(id))
        fetchSensors()
      } catch (err) {
        console.error("Error deleting sensor:", err)
        setError("Failed to delete sensor. Please try again.")
      }
    }
  }

  const handleOpenModal = (sensorId, sensorTypeId) => {
    setSelectedSensor(sensorId)
    setSelectedSensorType(sensorTypeId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setSelectedSensor(null)
    setIsModalOpen(false)
  }

  const columns = [
    { field: "name", headerName: "Name", flex: 0.2, minWidth: 150 },
    {
      field: "sensor_type_id",
      headerName: "Sensor Type",
      flex: 0.15,
      minWidth: 130,
      renderCell: (params) => {
        const sensorTypeName = sensorTypes[params.value] || "Unknown"
        return (
          <Chip
            label={sensorTypeName}
            color="primary"
            sx={{
              backgroundColor: "#b7d0fc",
              color: "#1967D2",
              padding: "4px 8px",
              borderRadius: "16px",
            }}
          />
        )
      },
    },
    { field: "imei", headerName: "IMEI", flex: 0.15, minWidth: 130 },
    { field: "serial_number", headerName: "Serial Number", flex: 0.15, minWidth: 130 },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.15,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          sx={{
            backgroundColor: params.value ? "#B1DDBD" : "#F9D5D3",
            color: params.value ? "#34A853" : "#EA4335",
            padding: "4px 8px",
            borderRadius: "16px",
            width: "90%",
          }}
        />
      ),
    },
    ...(hasPermission(userPermissions, 'sensor', PERMISSIONS.UPDATE) || hasPermission(userPermissions, 'sensor', PERMISSIONS.DELETE) ? [{
      field: "actions",
      headerName: "Actions",
      flex: 0.15,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            sx={{ color: "#6C757D" }}
            onClick={() => handleOpenModal(params.row.id, params.row.sensor_type_id)}
          >
            <VisibilityOutlined fontSize="inherit" />
          </IconButton>
          {hasPermission(userPermissions, 'sensor', PERMISSIONS.UPDATE) && (
            <IconButton 
              size="small" 
              sx={{ color: "#6C757D" }} 
              onClick={() => navigate(`/sensors/edit/${params.row.id}`)}
            >
              <EditOutlined fontSize="inherit" />
            </IconButton>
          )}
          {hasPermission(userPermissions, 'sensor', PERMISSIONS.DELETE) && (
            <IconButton 
              size="small" 
              sx={{ color: "#E31B54" }} 
              onClick={() => handleDelete(params.row.id)}
            >
              <DeleteOutlined fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      ),
    }] : [])
  ]

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 1 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Sensors
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        {hasPermission(userPermissions, 'sensor', PERMISSIONS.CREATE) && (
          <Tooltip title={sensorTypesAvailable ? "" : "Please create a sensor type first"}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => navigate("/sensors/create")}
                disabled={!sensorTypesAvailable}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#1967D2",
                  color: "#FFFFFF",
                  minWidth: "120px",
                  px: 2,
                  height: "32px",
                  borderRadius: "16px",
                  "&:hover": {
                    backgroundColor: "#1756B6",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#A9A9A9",
                    color: "#FFFFFF",
                  },
                }}
              >
                New sensor
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <DataGrid
        rows={sensors}
        columns={columns}
        pagination
        paginationMode="server"
        rowCount={totalItems}
        pageSizeOptions={[5, 10, 25]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(newModel) => {
          setPage(newModel.page)
          setPageSize(newModel.pageSize)
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        sx={{
          borderRadius: 6,
          borderColor: "background.primary",
          boxShadow: 1,
          color: "text.secondary",
          bgcolor: 'background.paper',
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus': {
              outline: 'none',
            },
            '&:focus-within': {
              outline: 'none',
            }
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderColor: 'background.default',
          },
        }}
      />
      <SensorDetailsModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        sensorId={selectedSensor}
        sensorTypeId={selectedSensorType}
      />
    </Box>
  )
}

export default Sensors
